import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Products", href: "#products" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const Logo = ({ scrolled }) => (
  <a href="#home" onClick={e => { e.preventDefault(); document.querySelector("#home")?.scrollIntoView({ behavior: "smooth" }); }}
    className="flex items-center gap-2.5 group" aria-label="SuperFast Auto Spares">
    <div className="relative w-9 h-9">
      <svg viewBox="0 0 36 36" fill="none" className="w-9 h-9">
        <circle cx="18" cy="18" r="18" fill="#E31837" />
        <path d="M10 22 L14 12 L18 19 L22 12 L26 22" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="18" cy="22" r="2" fill="white"/>
      </svg>
    </div>
    <div className="leading-tight">
      <span className={`text-[15px] tracking-tight block transition-colors duration-300 ${scrolled ? "text-gray-900" : "text-white"}`}
        style={{fontFamily:"'Syne',sans-serif",fontWeight:800}}>
        SuperFast <span className="text-red-500">Auto</span>
      </span>
      <span className={`text-[10px] uppercase tracking-[0.2em] font-medium block -mt-0.5 transition-colors duration-300 ${scrolled ? "text-gray-400" : "text-white/55"}`}>
        Spares
      </span>
    </div>
  </a>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const nav = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
      scrolled
        ? "bg-white/95 backdrop-blur-xl shadow-sm shadow-gray-200/80 border-b border-gray-100"
        : "bg-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-[70px]">
          <Logo scrolled={scrolled} />

          {/* Desktop */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              <button key={l.href} onClick={() => nav(l.href)}
                className={`text-sm font-semibold transition-colors duration-200 ${scrolled ? "text-gray-700 hover:text-red-600" : "text-white/90 hover:text-white"}`}>
                {l.label}
              </button>
            ))}
          </nav>

          <button onClick={() => nav("#contact")}
            className="hidden md:flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all duration-200 shadow-lg shadow-red-600/30 hover:shadow-red-600/50 hover:-translate-y-0.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
            </svg>
            Get a Quote
          </button>

          {/* Hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? "text-gray-800" : "text-white"}`}
            aria-label="Toggle menu">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {menuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/>}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-white border-t border-gray-100 shadow-xl">
            <nav className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-1">
              {navLinks.map(l => (
                <button key={l.href} onClick={() => nav(l.href)}
                  className="text-left text-gray-700 hover:text-red-600 font-semibold py-3 border-b border-gray-50 transition-colors text-sm">
                  {l.label}
                </button>
              ))}
              <button onClick={() => nav("#contact")}
                className="mt-3 bg-red-600 text-white font-bold py-3 rounded-xl text-sm">
                Get a Quote
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
